import React, { useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import Tree from '../../tree/Tree'
import useVariableTree from '../../../hooks/useVariableTree'
import { toWeightSyntax } from '../../../hooks/useBatchRegenerate'

export interface WeightPickerDialogProps {
  open: boolean
  onClose: () => void
  /** The chosen weight, always as `name()`. */
  onSelect: (weight: string) => void
  customer: string
  job: string
  /** Weights the folder's own tables already use, e.g. WeightCombo(). */
  inUse?: string[]
}

/**
 * Picks the weight for Regenerate Tables from the job's whole variable tree,
 * the same search as the case filter picker, with no codes to tick (Matt,
 * #1809: older jobs such as Prius have no weight metadata, so a weights-only
 * list was empty). The weight is written as `name()`.
 */
const WeightPickerDialog: React.FC<WeightPickerDialogProps> = ({ open, onClose, onSelect, customer, job, inUse = [] }) => {
  const variableTree = useVariableTree({ customer, job, singleVariable: true, autoExpandVariables: false, loadCodes: false })
  const [quick, setQuick] = useState('')
  const [typed, setTyped] = useState('')

  const ticked = variableTree.selectedVariables.find(v => v.type === 'Variable' || v.type === 'Codeframe')
  const chosen = toWeightSyntax(typed || quick || ticked?.value1 || '')

  const handleClose = () => {
    setQuick('')
    setTyped('')
    variableTree.resetChecked()
    onClose()
  }

  const handleSelect = () => {
    if (!chosen) return
    onSelect(chosen)
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose a weight</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tick the weight variable, or type its name.
        </Typography>
        {inUse.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Used by tables in this folder:</Typography>
            {inUse.map(w => (
              <Chip
                key={w}
                label={w}
                size="small"
                color="info"
                variant={toWeightSyntax(w) === chosen ? 'filled' : 'outlined'}
                onClick={() => { setQuick(w); setTyped(''); variableTree.resetChecked() }}
              />
            ))}
          </Stack>
        )}
        <Box sx={{ height: 340, border: 1, borderColor: 'divider', borderRadius: 1, p: 1, overflow: 'hidden' }}>
          {variableTree.isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading variables…</Typography>
          ) : variableTree.isError ? (
            <Alert severity="error">Failed to load the variables.</Alert>
          ) : (
            <Tree
              searchEnabled
              showTooltips
              searchPlaceholder="Search variables..."
              nodes={variableTree.nodes}
              expanded={variableTree.expanded}
              getCheckboxState={variableTree.getCheckboxState}
              onNodeExpandToggle={variableTree.onNodeExpandToggle}
              onNodeCheckedToggle={(itemId, node, isChecked) => {
                setQuick('')
                setTyped('')
                return variableTree.onNodeCheckedToggle(itemId, node, isChecked)
              }}
            />
          )}
        </Box>
        <TextField
          size="small"
          fullWidth
          label="Or type a weight"
          placeholder="e.g. WeightCombo"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          error={!!typed.trim() && !toWeightSyntax(typed)}
          helperText={typed.trim() && !toWeightSyntax(typed) ? 'Type one variable name, e.g. WeightCombo.' : undefined}
          sx={{ mt: 2 }}
        />
        <Typography variant="body2" sx={{ mt: 1.5 }} color={chosen ? 'text.primary' : 'text.secondary'}>
          {chosen ? <>Weight: <Box component="span" sx={{ fontFamily: 'monospace' }}>{chosen}</Box></> : 'Tick a variable to use as the weight.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="info" onClick={handleSelect} disabled={!chosen}>
          Select
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default WeightPickerDialog
